// BEGIN FILE: src/utils/vimshottari.ts
import { DateTime } from 'luxon';

export type DashaPlanet = 'Sun' | 'Moon' | 'Mars' | 'Mercury' | 'Jupiter' | 'Venus' | 'Saturn' | 'Rahu' | 'Ketu';
export type DashaLord = 'Ketu'|'Venus'|'Sun'|'Moon'|'Mars'|'Rahu'|'Jupiter'|'Saturn'|'Mercury';
export type DashaBand = { lord: DashaPlanet; start: string; end: string };

const DASHA_ORDER: DashaLord[] = ['Ketu','Venus','Sun','Moon','Mars','Rahu','Jupiter','Saturn','Mercury'];
const DASHA_YEARS: Record<DashaLord, number> = {
  Ketu:7, Venus:20, Sun:6, Moon:10, Mars:7, Rahu:18, Jupiter:16, Saturn:19, Mercury:17
};
const TOTAL_YEARS = 120;
const DASHA_YEAR_DAYS = 365.25636; // sidereal year
const DAY_MS = 86400000;
const YEAR_MS = DASHA_YEAR_DAYS * DAY_MS;
const NAK_LEN_DEG = 360 / 27; // 13°20'
const NAK_LORDS: DashaLord[] = Array.from({ length: 27 }, (_, i) => DASHA_ORDER[i % 9]);

const toMs = (x: string | Date | number) =>
  typeof x === 'number' ? x : (typeof x === 'string' ? new Date(x).getTime() : (x as Date).getTime());
const fromMs = (ms: number) => {
  if (!isFinite(ms) || isNaN(ms)) {
    console.error('[vimshottari] Invalid ms value:', ms);
    throw new Error(`Invalid time value: ${ms}`);
  }
  const iso = new Date(ms).toISOString();
  if (!iso) {
    throw new Error(`Failed to convert ms to ISO: ${ms}`);
  }
  return iso;
};

function mergeAdjacentSameLord(bands: DashaBand[]): DashaBand[] {
  const out: DashaBand[] = [];
  for (const b of bands) {
    const last = out[out.length - 1];
    if (last && last.lord === b.lord) {
      const endMs = Math.max(toMs(last.end), toMs(b.end));
      last.end = fromMs(endMs);
    } else {
      out.push({ ...b });
    }
  }
  return out;
}

function pickCurrent(bands: DashaBand[] | undefined, whenMs: number): DashaBand | null {
  if (!bands || bands.length === 0) return null;
  for (const b of bands) {
    const s = toMs(b.start), e = toMs(b.end);
    if (whenMs >= s && whenMs < e) return b;
  }
  return bands.reduce(
    (acc, b) => (toMs(b.start) <= whenMs && toMs(b.end) > (acc ? toMs(acc.end) : -Infinity) ? b : acc),
    bands[0]
  );
}

export function buildSubdasha(parent: DashaBand): DashaBand[] {
  const lord = parent.lord as DashaLord;
  const startIdx = DASHA_ORDER.indexOf(lord);
  const spanMs = Math.max(1, toMs(parent.end) - toMs(parent.start));
  let t = toMs(parent.start);
  const out: DashaBand[] = [];
  for (let k = 0; k < 9; k++) {
    const sub = DASHA_ORDER[(startIdx + k) % 9];
    const frac = DASHA_YEARS[sub] / TOTAL_YEARS;
    const dur = spanMs * frac;
    const s = t;
    const e = t + dur;
    out.push({ lord: sub as DashaPlanet, start: fromMs(s), end: fromMs(e) });
    t = e;
  }
  return mergeAdjacentSameLord(out);
}

export function computeMahadashaTimelineAround(
  birthISO: string,
  moonLonSiderealDeg: number,
  center: DateTime,
  yearsEachSide: number
): DashaBand[] {
  const birthDt = DateTime.fromISO(birthISO);
  if (!birthDt.isValid) {
    console.error('[vimshottari] Invalid birth ISO:', birthISO);
    throw new Error(`Invalid birth date: ${birthISO}`);
  }
  
  const birthMs = birthDt.toMillis();
  if (!isFinite(birthMs)) {
    console.error('[vimshottari] Invalid birthMs:', birthMs);
    throw new Error(`Invalid birth milliseconds: ${birthMs}`);
  }
  
  const viewStartMs = center.minus({ years: yearsEachSide }).toMillis();
  const viewEndMs = center.plus({ years: yearsEachSide }).toMillis();

  const lon = ((moonLonSiderealDeg % 360) + 360) % 360;
  const nakIndex = Math.floor(lon / NAK_LEN_DEG); // 0..26
  const nakStartLon = nakIndex * NAK_LEN_DEG;
  const posInNak = lon - nakStartLon;
  const fracElapsed = posInNak / NAK_LEN_DEG;

  const curLord = NAK_LORDS[nakIndex];
  const curYears = DASHA_YEARS[curLord];

  // Current MD started before birth by the elapsed fraction
  const elapsedMs = curYears * fracElapsed * YEAR_MS;
  const curMdStartMs = birthMs - elapsedMs;
  
  if (!isFinite(curMdStartMs) || isNaN(curMdStartMs)) {
    console.error('[vimshottari] Invalid curMdStartMs calculation:', {
      birthMs, moonLonSiderealDeg, lon, nakIndex, curLord, curYears, fracElapsed, elapsedMs, curMdStartMs
    });
    throw new Error(`Invalid MD start time calculated: ${curMdStartMs}`);
  }

  // Forward
  const forward: DashaBand[] = [];
  let idx = DASHA_ORDER.indexOf(curLord);
  let t = curMdStartMs;
  while (t < viewEndMs + YEAR_MS * 2) {
    const lord = DASHA_ORDER[idx % 9];
    const dur = DASHA_YEARS[lord] * YEAR_MS;
    const s = t, e = t + dur;
    forward.push({ lord: lord as DashaPlanet, start: fromMs(s), end: fromMs(e) });
    t = e;
    idx++;
  }

  // Backward
  const backward: DashaBand[] = [];
  let tend = curMdStartMs;
  let backIdx = (DASHA_ORDER.indexOf(curLord) - 1 + 9) % 9;
  
  while (tend > viewStartMs - YEAR_MS * 2) {
    // Proper modulo for negative numbers: ((n % m) + m) % m
    const idx = ((backIdx % 9) + 9) % 9;
    const lord = DASHA_ORDER[idx];
    const dur = DASHA_YEARS[lord] * YEAR_MS;
    const s = tend - dur;
    const e = tend;
    
    backward.push({ lord: lord as DashaPlanet, start: fromMs(s), end: fromMs(e) });
    tend = s;
    backIdx--;
  }

  const all = [...backward.reverse(), ...forward];
  return mergeAdjacentSameLord(all);
}

export function getCurrentDashaLevels(birthISO: string, moonLonSiderealDeg: number, centerISO: string) {
  const center = DateTime.fromISO(centerISO);
  const mdList = computeMahadashaTimelineAround(birthISO, moonLonSiderealDeg, center, 70);
  const md = pickCurrent(mdList, center.toMillis());
  if (!md) return null;
  const adList = buildSubdasha(md);
  const ad = pickCurrent(adList, center.toMillis());
  if (!ad) return { md, ad: null, pd: null, sd: null };
  const pdList = buildSubdasha(ad);
  const pd = pickCurrent(pdList, center.toMillis());
  if (!pd) return { md, ad, pd: null, sd: null };
  const sdList = buildSubdasha(pd);
  const sd = pickCurrent(sdList, center.toMillis());
  return { md, ad, pd, sd };
}
